/** @jsx jsx */
import { css, jsx, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { useLocation } from "@gatsbyjs/reach-router";
import { useEffect, useState, Fragment } from "react";
import { useTranslation } from "react-i18next";

import { usePageContext } from "../../../../../plugins/@whitespace/gatsby-theme-wordpress-basic/src/hooks/page-context";
import DefaultHamburgerMenu from "../../../../../plugins/@whitespace/gatsby-theme-wordpress-basic/src/wsui/components/HeaderHamburgerMenu.jsx";
import DefaultLogo from "../../../../../plugins/@whitespace/gatsby-theme-wordpress-basic/src/wsui/components/HeaderLogo.jsx";
import {
  Clickable,
  Icon,
  PageGrid,
  PageGridItem,
  Link,
  handleComponentsProp,
  useThemeProps,
  usePrevious,
  Button,
} from "../../../../../wsui/base";
import HeaderHelpMenu from "../../../../HeaderHelpMenu.jsx";
import HeaderSearchField from "../../../../wsui/components/HeaderSearchField.jsx";

import HeaderMainMenu from "./HeaderMainMenu.jsx";

const DefaultHamburgerMenuToggle = styled(Clickable)``;

const SkipToMainLink = styled(Button)`
  position: absolute;
  left: 50%;
  transform: translateY(-550%);
  transition: transform 0.3s;
  z-index: 1;
  &:focus {
    transform: translateY(0%);
  }
`;

export default function Header(props) {
  const { t } = useTranslation();
  const theme = useTheme();

  props = useThemeProps({
    props,
    name: "Header",
  });

  let {
    color = "primary",
    components,
    disableHamburgerMenu = false,
    ...restProps
  } = props;

  let { HamburgerMenu, HamburgerMenuToggle, Logo, MainMenu } =
    handleComponentsProp(components, {
      HamburgerMenu: DefaultHamburgerMenu,
      HamburgerMenuToggle: DefaultHamburgerMenuToggle,
      Logo: DefaultLogo,
      MainMenu: HeaderMainMenu,
      HeaderHelpMenu,
    });

  const location = useLocation();
  const prevLocation = usePrevious(location);

  const { isSearch } = usePageContext();

  const [hamburgerOpen, setHamburgerOpen] = useState(false);
  useEffect(() => {
    document.body.style.overflow = hamburgerOpen ? "hidden" : "";
  }, [hamburgerOpen]);

  useEffect(() => {
    if (location !== prevLocation) {
      setHamburgerOpen(false);
    }
  }, [location, prevLocation]);

  return (
    <Fragment>
      {/* <Helmet>
        <script
          id="Cookiebot"
          src="https://consent.cookiebot.com/uc.js"
          data-cbid="17c0e9fd-9347-480f-bd26-befb042cae87"
          data-blockingmode="auto"
          type="text/javascript"
        />
      </Helmet> */}
      <header {...restProps}>
        <SkipToMainLink variant="primary" href="#main">
          Gå direkt till innehåll
        </SkipToMainLink>
        {HeaderHelpMenu && (
          <HeaderHelpMenu
            css={css`
              @media (max-width: 640px) {
                display: none;
              }
            `}
          />
        )}
        <PageGrid
          css={css`
            background-color: ${theme.getColor(color)};
            color: ${theme.getColor([color, "text"])};
          `}
        >
          <PageGridItem colspan={12}>
            <div
              css={css`
                display: grid;
                grid-template-columns: max-content minmax(0, 1fr) 1fr max-content;
                grid-template-areas: "logo main-menu search flyout";
                padding-block: 1.5rem;
                gap: ${theme.getLength([4, 8])};
                align-items: center;

                @media (max-width: 840px) {
                  grid-template-columns: max-content minmax(0, 1fr) max-content max-content;
                }
              `}
            >
              <Logo
                link="/"
                css={css`
                  grid-area: logo;
                `}
              />
              <Link
                uri="/sok"
                aria-label="Sök"
                color={[color, "text"]}
                css={css`
                  grid-area: search;
                  @media (min-width: 841px) {
                    display: none;
                  }
                `}
              >
                <Icon
                  name="search"
                  size="2rem"
                  alt="Sök"
                  color={[color, "text"]}
                />
              </Link>
              <MainMenu
                css={css`
                  align-self: stretch;
                  display: grid;
                  align-content: center;
                  grid-template-columns: minmax(0, 1fr);
                  @supports not
                    selector(:has(.wsui-inline-list.wsui-is-content-visible)) {
                    border-left: 2px solid ${theme.getColor("border")};
                    padding-left: ${theme.getLength([4, 8])};
                  }
                  &:has(.wsui-inline-list.wsui-is-content-visible) {
                    border-left: 2px solid ${theme.getColor("border")};
                    padding-left: ${theme.getLength([4, 8])};
                  }
                `}
              />
              <HeaderSearchField
                css={css`
                  grid-area: search;

                  @media (max-width: 840px) {
                    display: none;
                  }

                  display: ${isSearch ? "none" : null};
                `}
              />
              {!disableHamburgerMenu && (
                <HamburgerMenuToggle
                  onClick={() => {
                    setHamburgerOpen(true);
                  }}
                  aria-label={t("menu")}
                  aria-expanded={String(hamburgerOpen)}
                  aria-controls={"header-hamburger-menu"}
                  css={css`
                    grid-area: flyout;
                    cursor: pointer;
                  `}
                >
                  <Icon name="menu" size="2rem" />
                </HamburgerMenuToggle>
              )}
            </div>
          </PageGridItem>
        </PageGrid>
        {!disableHamburgerMenu && (
          <HamburgerMenu
            open={hamburgerOpen}
            onClose={() => setHamburgerOpen(false)}
            id={"header-hamburger-menu"}
          />
        )}
      </header>
    </Fragment>
  );
}
